import { PlainObject } from '@/types'
import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { Media, Page, Person } from '@/payload-types'
import { Slide } from 'yet-another-react-lightbox'

function isMedia(media: number | Media): media is Media {
  return typeof media === 'object' && media !== null && 'id' in media
}

function isValidImage(image: Media): image is Media & {
  url: string
  width: number
  height: number
} {
  return (
    typeof image.url === 'string' &&
    typeof image.width === 'number' &&
    typeof image.height === 'number'
  )
}

function isPage(page: number | Page): page is Page {
  return typeof page === 'object' && page !== null && 'id' in page
}

function isPerson(person: number | Person): person is Person {
  return typeof person === 'object' && person !== null && 'id' in person
}

/**
 * Simple object check.
 * @param item
 * @returns {boolean}
 */
function isObject(item: unknown): item is PlainObject {
  return typeof item === 'object' && item !== null && !Array.isArray(item)
}

/**
 * Deep merge two objects.
 * @param target
 * @param source
 */
function deepMerge<T extends PlainObject, R extends PlainObject>(target: T, source: R): T {
  const output = { ...target } as PlainObject

  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach((key) => {
      if (isObject(source[key])) {
        if (key in target) {
          output[key] = deepMerge(target[key] as PlainObject, source[key] as PlainObject)
        } else {
          output[key] = source[key]
        }
      } else {
        output[key] = source[key]
      }
    })
  }

  return output as T
}

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

function convertMediaForLightbox(images: Media[]): Slide[] {
  return images.filter(isValidImage).map((image) => ({
    src: image.url,
    alt: image.alt ?? undefined,
    width: image.width,
    height: image.height,
  }))
}

function slugify(str: string) {
  return str
    .toLowerCase()
    .normalize('NFD') // decompose "é" → "e" + combining accent
    .replaceAll(/\p{Diacritic}/gu, '') // strip the combining accent marks
    .trim()
    .replaceAll(/[^\w\s-]/g, '')
    .replaceAll(/[\s_]+/g, '-')
    .replaceAll(/^-+|-+$/g, '')
}

export {
  isObject,
  isMedia,
  isPage,
  isPerson,
  isValidImage,
  deepMerge,
  cn,
  convertMediaForLightbox,
  slugify,
}
